import * as z from 'zod';

/**
 * Gets the name of the calling class and method from the stack trace
 * Returns format: "ClassName.methodName"
 */
function getCallerName(): string {
  const error = new Error();
  const stack = error.stack?.split('\n') || [];
  // Stack trace format: "at ClassName.methodName (file:line:column)"
  // We need to find the first frame that's not this function or createApiResponseParser
  for (const line of stack) {
    const match = line.match(/at\s+(?:new\s+)?(\w+(?:\.\w+)?)/);
    if (
      match &&
      !match[1].includes('createApiResponseParser') &&
      !match[1].includes('getCallerName')
    ) {
      return match[1] || 'unknown';
    }
  }
  return 'unknown';
}

/**
 * Creates a reusable parser function for API responses
 * Automatically detects the calling method name from stack trace
 * @param schema - Zod schema to validate the response
 * @returns Parse function that validates and extracts data from API response
 */
export function createApiResponseParser<TData>(
  schema: z.ZodType<{ data: TData }>,
): (value: unknown) => TData {
  const methodName = getCallerName();

  return (value: unknown) => {
    const result = schema.safeParse(value);

    if (result.success) {
      return result.data.data;
    }

    throw new Error(`[${methodName}] Invalid API response structure: ${result.error.message}`);
  };
}

/**
 * Creates a reusable parser function for API responses that also returns pagination metadata
 * Automatically detects the calling method name from stack trace
 * @param schema - Zod schema to validate the response
 * @returns Parse function that validates and extracts data and metadata from API response
 */
export function createPaginatedApiResponseParser<TData, TMeta>(
  schema: z.ZodType<{ data: TData, meta?: TMeta }>,
): (value: unknown) => { data: TData, meta?: TMeta } {
  const methodName = getCallerName();

  return (value: unknown) => {
    const result = schema.safeParse(value);

    if (result.success) {
      return { data: result.data.data, meta: result.data.meta };
    }

    throw new Error(`[${methodName}] Invalid paginated API response structure: ${result.error.message}`);
  };
}
